import { useState, useEffect } from "react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { CheckCircle, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * PodPricingTiers — renders 3-month, 6-month and 12-month subscription tiers
 * with live ₦/USD conversion. Click → Stripe checkout (returns to /pod/success).
 *
 * Props:
 *   onSelect?      — called with the chosen tier key (e.g. "12_months") if you
 *                    want a "select & continue" flow instead of immediate checkout.
 *   compact        — render a smaller version for embedding under a hero.
 */
export default function PodPricingTiers({ onSelect = null, compact = false }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkingOut, setCheckingOut] = useState(null);

  useEffect(() => {
    api.get("/pod/pricing").then(r => { setTiers(r.data.tiers || []); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const startCheckout = async (tierKey) => {
    if (!user) {
      // Send them to login → they come back here
      sessionStorage.setItem("pod_intent_tier", tierKey);
      navigate("/login?next=/pod");
      return;
    }
    setCheckingOut(tierKey);
    try {
      const { data } = await api.post("/pod/checkout", {
        tier: tierKey,
        origin_url: window.location.origin,
      });
      if (data.url) {
        window.location.href = data.url;
      } else {
        toast.error("Could not start checkout — please try again");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
    }
    setCheckingOut(null);
  };

  if (loading) return <div className="text-sm text-slate-500 text-center py-6">Loading pricing…</div>;

  return (
    <div data-testid="pod-pricing-tiers">
      <div className="text-center mb-8">
        <p className="text-xs uppercase tracking-[0.25em] text-amber-600 font-bold mb-2">Membership</p>
        <h2 className={`font-bold text-slate-950 ${compact ? "text-2xl" : "text-3xl sm:text-4xl"}`} style={{ fontFamily: "'Outfit', sans-serif" }}>
          Choose your POD plan
        </h2>
        <p className="text-sm text-slate-600 mt-3 max-w-xl mx-auto">
          Equivalent to <b>₦10,000 / month</b> base rate — paid in 3, 6, or 12-month bundles. Cancel anytime.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {tiers.map((t, idx) => {
          const featured = t.tier === "12_months";
          return (
            <div
              key={t.tier}
              data-testid={`pod-tier-${t.tier}`}
              className={`relative border ${featured ? "border-blue-800 bg-slate-950 text-white" : "border-slate-200 bg-white"} p-6 flex flex-col`}
            >
              {featured && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-amber-500 text-slate-950 text-[10px] uppercase tracking-widest font-bold px-3 py-1">
                  Best value
                </span>
              )}
              <h3 className={`text-xl font-bold mb-1 ${featured ? "text-white" : "text-slate-950"}`} style={{ fontFamily: "'Outfit', sans-serif" }}>
                {t.label}
              </h3>
              <p className={`text-xs ${featured ? "text-amber-300" : "text-slate-500"} font-semibold uppercase tracking-wider`}>
                {t.savings_label}
              </p>

              <div className="my-6">
                <div className="flex items-baseline gap-1">
                  <span className={`text-4xl font-bold ${featured ? "text-white" : "text-slate-950"}`}>
                    ₦{t.price_ngn.toLocaleString()}
                  </span>
                </div>
                <p className={`text-xs ${featured ? "text-blue-200" : "text-slate-500"} mt-1`}>
                  ≈ <b>${t.price_usd.toFixed(2)} USD</b> · ₦{t.monthly_ngn.toLocaleString()}/month
                </p>
              </div>

              <ul className={`space-y-2 mb-6 text-sm ${featured ? "text-blue-100" : "text-slate-700"}`}>
                {[
                  "Full Webinar Centre access",
                  "Resource Library downloads",
                  "AI Template Generator (PDF/Word/Excel/PPT)",
                  "Virtual Roundtable community",
                  "Curated weekly updates",
                ].map(b => (
                  <li key={b} className="flex items-start gap-2">
                    <CheckCircle weight="fill" size={16} className={`flex-shrink-0 mt-0.5 ${featured ? "text-amber-300" : "text-emerald-600"}`} />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-auto">
                <Button
                  disabled={checkingOut === t.tier}
                  onClick={() => onSelect ? onSelect(t.tier) : startCheckout(t.tier)}
                  data-testid={`pod-tier-cta-${t.tier}`}
                  className={`w-full rounded-none py-6 gap-2 ${featured ? "bg-amber-500 text-slate-950 hover:bg-amber-400" : "bg-blue-800 text-white hover:bg-blue-900"}`}
                >
                  {checkingOut === t.tier ? "Starting checkout…" : <>Join — ₦{t.price_ngn.toLocaleString()} <ArrowRight size={14} weight="bold" /></>}
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      <p className="text-xs text-slate-500 text-center mt-6">
        Secure checkout via Stripe. NGN amounts converted to USD at the live mid-market rate at the time of payment.
      </p>
    </div>
  );
}
